/**
 * Condição dos alarmes personalizados — backend NautMax (`/geo/v1/custom-alarms`).
 *
 * Falava direto com `/api/attributes/computed` do Traccar até 2026-08-10. O Traccar exige
 * administrador nos quatro verbos de escrita (`AttributeResource` chama `checkAdmin` em
 * criar, editar, remover e testar), então a revenda batia em 403 e a tela nem montava.
 *
 * Agora a escrita passa pelo nosso backend, que assina com a sessão administrativa depois
 * de conferir duas coisas que o Traccar deixou de conferir: se os veículos estão no acesso
 * de quem pediu, e se a expressão é a forma canônica que esta tela gera. Detalhes e o
 * porquê de cada trava em `backend/app/routers/custom_alarms.py`.
 *
 * A LEITURA também vem do backend, e não do Traccar, porque a lista precisa ser escopada
 * por tenant: um `GET all=true` no Traccar devolveria a condição de outra revenda.
 */

export interface ComputedAttribute {
  id: number;
  /** Guardamos o nome do alarme aqui, para achar a regra sem reler a expressão. */
  description: string;
  /** Para alarme personalizado é sempre `alarm`. */
  attribute: string;
  expression: string;
  type: 'string' | 'number' | 'boolean';
  /** Devolvido pelo 6.14.5; reenviado sem alteração. */
  priority?: number;
  /** O backend responde se este papel pode editar esta condição. */
  editavel?: boolean;
}

export type ComputedAttributeInput = Omit<ComputedAttribute, 'id' | 'editavel'> & { id?: number };

export interface AlcanceInput {
  deviceIds: number[];
  groupIds: number[];
}

const BASE = '/geo/v1/custom-alarms';

/** Preserva a mensagem do backend: é ela que explica por que a condição foi recusada. */
async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'include', ...init });
  if (!r.ok) {
    let detalhe = '';
    try {
      const corpo = await r.json();
      detalhe = typeof corpo?.detail === 'string' ? corpo.detail : '';
    } catch {
      // corpo não-JSON: fica só o status, que ainda diz algo
    }
    throw new Error(detalhe || `Servidor respondeu ${r.status}.`);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const computedAttributesApi = {
  /**
   * Já vem escopado por papel: admin vê todas, revenda vê as do próprio tenant.
   * Com `deviceId`, só as que se aplicam àquele veículo (usado pelo diagnóstico).
   */
  list: (deviceId?: number) =>
    req<ComputedAttribute[]>(deviceId ? `${BASE}?deviceId=${deviceId}` : BASE),

  create: (data: ComputedAttributeInput) => req<ComputedAttribute>(BASE, json('POST', data)),

  update: (id: number, data: ComputedAttributeInput) =>
    req<ComputedAttribute>(`${BASE}/${id}`, json('PUT', { ...data, id })),

  delete: (id: number) => req<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  /**
   * A quais veículos e grupos a condição se aplica. Sem isso o Traccar nunca avalia a
   * expressão, então não é acessório: é o que faz a regra existir.
   *
   * Substitui os `permissionsApi.link` de `attributeId`, que uma revenda não consegue
   * fazer (o vínculo exige acesso de escrita ao atributo computado, que é de admin).
   */
  setAlcance: (id: number, alcance: AlcanceInput) =>
    req<void>(`${BASE}/${id}/alcance`, json('PUT', alcance)),

  /**
   * Avalia contra a última posição do veículo sem gravar nada.
   * `null` é resposta legítima: a condição não bateu agora.
   */
  test: async (deviceId: number, data: ComputedAttributeInput): Promise<string | null> => {
    const r = await req<{ valor: string | null }>(
      `${BASE}/test?deviceId=${deviceId}`,
      json('POST', data),
    );
    const v = r?.valor;
    return v === undefined || v === null || v === '' ? null : String(v).trim() || null;
  },
};
